/**
 * Web Storage som best-effort-cache i stället för kraschkälla.
 *
 * Det är **åtkomsten** som kastar, inte bara operationen: i Safari med "blockera alla cookies"
 * (och i vissa privatlägen) ger redan `window.sessionStorage` en `SecurityError`. Ett bart
 * `sessionStorage.getItem(...)` i en mount-effekt tar därför ner hela trädet till närmaste
 * ErrorBoundary — användaren får en felskärm i stället för inloggningsrutan, och ingenting av det
 * syns i våra loggar. Hittad 2026-08-17 när ett ADR-117-test mockade storage till att kasta.
 *
 * Den enda regel som betyder något här: **en misslyckad läsning ska ge exakt samma resultat som en
 * tom läsning.** Ger den något annat — kastar, eller returnerar en sentinel anroparen måste tolka —
 * flyttas kraschen bara ett steg upp, till en callsite som inte heller kan göra något åt saken.
 * Skrivningar är no-op av samma skäl: den som inte får spara ett val ska bete sig som den som inte
 * har sparat något ännu.
 *
 * `hooks/useVersionPoll.ts` och `lib/authProxy.ts` har egna vakter och ska INTE flyttas hit — deras
 * fallbackvärden (`claimLoginReload` returnerar `false`, inte `null`) bär beslut som är specifika
 * för respektive mekanism och som en generisk hjälpare skulle radera.
 *
 * Nytt värde som ska överleva en omladdning? Gå via den här modulen, aldrig via `sessionStorage`/
 * `localStorage` direkt.
 */

function read(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function write(storage: () => Storage, key: string, value: string): void {
  try {
    storage().setItem(key, value);
  } catch {
    // Blockerad storage, eller full kvot. Ingendera är något anroparen kan åtgärda.
  }
}

function remove(storage: () => Storage, key: string): void {
  try {
    storage().removeItem(key);
  } catch {
    // Går den inte att läsa finns det heller inget kvar att rensa.
  }
}

// Getters, inte referenser: `sessionStorage` måste läsas INUTI try-blocket, annars kastar
// själva uppslaget innan vakten hunnit ta över.
const session = () => sessionStorage;
const local = () => localStorage;

export function readSession(key: string): string | null {
  return read(session, key);
}

export function writeSession(key: string, value: string): void {
  write(session, key, value);
}

export function removeSession(key: string): void {
  remove(session, key);
}

export function readLocal(key: string): string | null {
  return read(local, key);
}

export function writeLocal(key: string, value: string): void {
  write(local, key, value);
}

export function removeLocal(key: string): void {
  remove(local, key);
}

/**
 * Alla nycklar i `localStorage` som matchar ett prefix. Tom lista när storage inte går att läsa.
 *
 * Finns för `AuthContext.signOut`, som sveper ut gamla `supabase*`-nycklar (pre-SSO-tokens). Både
 * uppräkningen och borttagningen kastar när storage är blockerad, så svepet behövde en vakt som
 * täcker båda — inte bara borttagningen.
 *
 * Använder `length` + `key(i)` och INTE `Object.keys(localStorage)`. Den senare bygger på att
 * Storage exponerar sina nycklar som egna uppräkningsbara properties — sant i riktiga webbläsare,
 * men **falskt i jsdom**, som svarar `[]` med data på plats. Svepet gick alltså inte att skriva ett
 * test för, och ett tomt svep hade sett grönt ut hela vägen. Den här formen är den specificerade
 * Storage-API:n och beter sig likadant i båda.
 *
 * Läser färdigt innan anroparen hinner ta bort något — index förskjuts vid `removeItem`.
 */
export function localKeysWithPrefix(prefix: string): string[] {
  return keysWithPrefix(local, prefix);
}

/** Som `localKeysWithPrefix`, fast för `sessionStorage`. */
export function sessionKeysWithPrefix(prefix: string): string[] {
  return keysWithPrefix(session, prefix);
}

function keysWithPrefix(storage: () => Storage, prefix: string): string[] {
  try {
    const store = storage();
    const keys: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key?.startsWith(prefix)) {
        keys.push(key);
      }
    }
    return keys;
  } catch {
    return [];
  }
}
